import React, { useState, useEffect } from 'react';
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    MarkerType,
    Node,
    Edge,
    NodeTypes
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Box from '@mui/joy/Box';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import Chip from '@mui/joy/Chip';
import Typography from '@mui/joy/Typography';

import CustomActionNode, { ActionNodeData, ActionLog, ActionType, getActionColor } from './CustomActionNode';
import ApiService from "../../axios/Bookings-Axios";

const nodeTypes: NodeTypes = { custom: CustomActionNode };

interface LeadActivityFlowProps {
    lead_id: number;
}

const LeadActivityFlow: React.FC<LeadActivityFlowProps> = ({ lead_id }) => {
    const [nodes, setNodes] = useState<Node<ActionNodeData>[]>([]);
    const [edges, setEdges] = useState<Edge[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [activeFilters, setActiveFilters] = useState<ActionType[]>([]);

    useEffect(() => {
        const fetchAndProcessLogs = async () => {
            if (!lead_id) return;

            setLoading(true);
            setError(null);

            try {
                const actionLogs: ActionLog[] = await ApiService.getActionLogByLeadID(lead_id);
                const sortedLogs = actionLogs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

                const newNodes: Node<ActionNodeData>[] = [];

                sortedLogs.forEach((log, index) => {
                    newNodes.push({
                        id: log.id.toString(),
                        type: 'custom',
                        position: { x: 0, y: 0 }, // initial placeholder
                        data: {
                            action: log.action,
                            context: log.context,
                            createdAt: log.created_at,
                            userName: log.user_name
                        },
                    });
                });

                if (newNodes.length === 0) {
                    newNodes.push({
                        id: "0",
                        type: 'custom',
                        position: { x: 0, y: 0 },
                        data: {
                            action: "No Actions Found",
                            context: "No actions found for this lead. This feature was implemented AFTER 1st September 2025. Older leads don't have any actions.",
                            createdAt: null,
                            userName: null
                        },
                    });
                }

                setNodes(newNodes);

            } catch (err) {
                setError("Failed to fetch action logs. Please try again later.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAndProcessLogs();
    }, [lead_id]);

    // Toggle filter
    const toggleFilter = (action: ActionType) => {
        setActiveFilters(prev =>
            prev.includes(action)
                ? prev.filter(a => a !== action)
                : [...prev, action]
        );
    };

    // Compute available actions for the filter bar
    const availableActions = Array.from(new Set(nodes.map(node => node.data.action)));

    // Filter nodes based on active filters
    const filteredNodes = activeFilters.length > 0
        ? nodes.filter(node => activeFilters.includes(node.data.action))
        : nodes;

    // Re-layout nodes vertically for even spacing
    const reLayoutNodes = filteredNodes.map((node, index) => ({
        ...node,
        position: { x: 0, y: index * 250 } // constant vertical spacing
    }));

    // Re-create edges between visible nodes only
    const reCreatedEdges: Edge[] = [];
    for (let i = 1; i < reLayoutNodes.length; i++) {
        const prevNode = reLayoutNodes[i - 1];
        const currentNode = reLayoutNodes[i];

        reCreatedEdges.push({
            id: `e-${prevNode.id}-${currentNode.id}`,
            source: prevNode.id,
            target: currentNode.id,
            type: 'smoothstep',
            draggable: false,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#636b74' },
            style: { stroke: '#636b74' }
        });
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2, height: '80vh' }}>
                <Alert color="danger">{error}</Alert>
            </Box>
        );
    }

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Filter Bar */}
            <Box
                role="region"
                aria-label="Filter lead actions"
                sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    gap: 1,
                    p: 2,
                    backgroundColor: 'background.surface',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'neutral.outlinedBorder', borderRadius: 3 }
                }}
            >
                <Typography level="body-md" sx={{ mr: 2, flexShrink: 0 }}>Filter:</Typography>
                {availableActions.map(action => {
                    const color = getActionColor(action);
                    return (
                        <Chip
                            key={action}
                            variant={activeFilters.includes(action) ? 'soft' : 'outlined'}
                            color={color}
                            onClick={() => toggleFilter(action)}
                            sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                            aria-pressed={activeFilters.includes(action)}
                            role="button"
                        >
                            {action}
                        </Chip>
                    );
                })}
            </Box>

            {/* React Flow */}
            <Box sx={{ flexGrow: 1 }}>
                <ReactFlowProvider>
                    <ReactFlow
                        nodes={reLayoutNodes}
                        edges={reCreatedEdges}
                        nodeTypes={nodeTypes}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        panOnDrag={true}
                        zoomOnScroll={true}
                        fitView
                        proOptions={{ hideAttribution: true }}
                    >
                        <Background variant="dots" gap={12} size={1} />
                    </ReactFlow>
                </ReactFlowProvider>
            </Box>
        </div>
    );
};

export default LeadActivityFlow;
